import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { Loader2, Plus, Pencil, Trash2, DollarSign, User, Building2, Check, ChevronsUpDown, Search } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format currency in INR
const formatINR = (amount) => {
  if (!amount) return '₹0';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

// Component for adding investor to a deal
export function AddInvestorToDeal({ 
  isOpen, 
  onClose, 
  deal, 
  investors = [], // All approved investors
  existingCommitments = [], // Commitments already made to this deal
  onSuccess 
}) {
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter out investors who have already committed
  const committedEmails = existingCommitments.map(c => c.user_email?.toLowerCase());
  const availableInvestors = investors.filter(
    inv => !committedEmails.includes(inv.email?.toLowerCase())
  );

  // Filter by search
  const filteredInvestors = availableInvestors.filter(inv => {
    const fullName = `${inv.first_name || ''} ${inv.last_name || ''}`.toLowerCase();
    const email = (inv.email || '').toLowerCase();
    const query = searchQuery.toLowerCase();
    return fullName.includes(query) || email.includes(query);
  });

  const handleSave = async () => {
    if (!selectedInvestor) {
      toast.error('Please select an angel');
      return;
    }
    if (!amount || parseInt(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSaving(true);
    try {
      const userEmail = localStorage.getItem('user_email');
      await axios.post(
        `${API}/admin/deals/${deal.id}/commitments?admin_email=${userEmail}`,
        {
          user_email: selectedInvestor.email,
          amount_inr: parseInt(amount)
        }
      );
      toast.success('Investment added successfully!');
      onSuccess();
      onClose();
      // Reset form
      setSelectedInvestor(null);
      setAmount('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add investment');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedInvestor(null);
    setAmount('');
    setSearchQuery('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#0F172A] flex items-center gap-2">
            <User className="h-5 w-5 text-brand" />
            Add Angel
          </DialogTitle>
          <DialogDescription className="text-[#64748B]">
            Add an angel commitment to <span className="font-semibold text-[#0F172A]">{deal?.brand_name || deal?.name}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Investor Search Dropdown */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Select Angel *</Label>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="w-full justify-between bg-white border-[#E2E8F0]"
                >
                  {selectedInvestor ? (
                    <span>{selectedInvestor.first_name} {selectedInvestor.last_name} ({selectedInvestor.email})</span>
                  ) : (
                    <span className="text-gray-500">Search angels...</span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0 bg-white" align="start">
                <Command>
                  <CommandInput 
                    placeholder="Search by name or email..." 
                    value={searchQuery}
                    onValueChange={setSearchQuery}
                  />
                  <CommandList>
                    <CommandEmpty>No angels found.</CommandEmpty>
                    <CommandGroup>
                      {filteredInvestors.map((investor) => (
                        <CommandItem
                          key={investor.email}
                          value={investor.email}
                          onSelect={() => {
                            setSelectedInvestor(investor);
                            setOpen(false);
                          }}
                        >
                          <Check
                            className={`mr-2 h-4 w-4 ${
                              selectedInvestor?.email === investor.email ? "opacity-100" : "opacity-0"
                            }`}
                          />
                          <div>
                            <div className="font-medium">{investor.first_name} {investor.last_name}</div>
                            <div className="text-xs text-gray-500">{investor.email}</div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {availableInvestors.length === 0 && (
              <p className="text-xs text-amber-600">All angels have already committed to this deal.</p>
            )}
          </div>

          {/* Amount Field - only show after investor is selected */}
          {selectedInvestor && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Commitment Amount (INR) *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                <Input
                  type="text"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ''))}
                  placeholder="Enter amount"
                  className="pl-8 bg-white border-[#E2E8F0]"
                />
              </div>
              {amount && (
                <p className="text-xs text-gray-500">Amount: {formatINR(parseInt(amount))}</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || !selectedInvestor || !amount}
            className="!bg-[#10B981] hover:!bg-[#059669] !text-white"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Adding...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Add Investment
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Component for adding deal investment to a user
export function AddDealToInvestor({ 
  isOpen, 
  onClose, 
  user,
  deals = [], // All approved deals
  existingCommitments = [], // Commitments this user has already made
  onSuccess 
}) {
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter out deals user has already committed to
  const committedDealIds = existingCommitments.map(c => c.deal_id);
  const availableDeals = deals.filter(
    deal => !committedDealIds.includes(deal.id)
  );

  // Filter by search
  const filteredDeals = availableDeals.filter(deal => {
    const name = (deal.brand_name || deal.name || deal.legal_name || '').toLowerCase();
    const oneLiner = (deal.one_liner || '').toLowerCase();
    const query = searchQuery.toLowerCase();
    return name.includes(query) || oneLiner.includes(query);
  });

  const handleSave = async () => {
    if (!selectedDeal) {
      toast.error('Please select a startup/deal');
      return;
    }
    if (!amount || parseInt(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSaving(true);
    try {
      const adminEmail = localStorage.getItem('user_email');
      await axios.post(
        `${API}/admin/users/${user.email}/commitments?admin_email=${adminEmail}`,
        {
          deal_id: selectedDeal.id,
          amount_inr: parseInt(amount)
        }
      );
      toast.success('Investment added successfully!');
      onSuccess();
      onClose();
      // Reset form
      setSelectedDeal(null);
      setAmount('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add investment');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedDeal(null);
    setAmount('');
    setSearchQuery('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#0F172A] flex items-center gap-2">
            <Building2 className="h-5 w-5 text-brand" />
            Add Investment
          </DialogTitle>
          <DialogDescription className="text-[#64748B]">
            Add investment for <span className="font-semibold text-[#0F172A]">{user?.first_name} {user?.last_name}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Deal Search Dropdown */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Select Startup/Deal *</Label>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="w-full justify-between bg-white border-[#E2E8F0]"
                >
                  {selectedDeal ? (
                    <span>{selectedDeal.name || selectedDeal.brand_name || selectedDeal.legal_name}</span>
                  ) : (
                    <span className="text-gray-500">Search startups...</span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0 bg-white" align="start">
                <Command>
                  <CommandInput 
                    placeholder="Search startups..." 
                    value={searchQuery}
                    onValueChange={setSearchQuery}
                  />
                  <CommandList>
                    <CommandEmpty>No startups found.</CommandEmpty>
                    <CommandGroup>
                      {filteredDeals.map((deal) => (
                        <CommandItem
                          key={deal.id}
                          value={deal.id}
                          onSelect={() => {
                            setSelectedDeal(deal);
                            setOpen(false);
                          }}
                        >
                          <Check
                            className={`mr-2 h-4 w-4 ${
                              selectedDeal?.id === deal.id ? "opacity-100" : "opacity-0"
                            }`}
                          />
                          <div>
                            <div className="font-medium">{deal.brand_name || deal.name || deal.legal_name}</div>
                            <div className="text-xs text-gray-500">{deal.category || deal.type}</div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {availableDeals.length === 0 && (
              <p className="text-xs text-amber-600">This angel has already committed to all available deals.</p>
            )}
          </div>

          {/* Amount Field - only show after deal is selected */}
          {selectedDeal && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Commitment Amount (INR) *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                <Input
                  type="text"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ''))}
                  placeholder="Enter amount"
                  className="pl-8 bg-white border-[#E2E8F0]"
                />
              </div>
              {amount && (
                <p className="text-xs text-gray-500">Amount: {formatINR(parseInt(amount))}</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || !selectedDeal || !amount}
            className="!bg-[#10B981] hover:!bg-[#059669] !text-white"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Adding...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Add Investment
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Component for editing a commitment
export function EditCommitmentDialog({ 
  isOpen, 
  onClose, 
  commitment,
  isAdmin = false,
  onSuccess 
}) {
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && commitment) {
      setAmount(commitment.amount_inr?.toString() || '');
    }
  }, [isOpen, commitment]);

  const handleSave = async () => {
    if (!amount || parseInt(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSaving(true);
    try {
      const userEmail = localStorage.getItem('user_email');
      const endpoint = isAdmin 
        ? `${API}/admin/commitments/${commitment.id}?admin_email=${userEmail}`
        : `${API}/commitments/${commitment.id}?email=${userEmail}`;
      
      await axios.put(endpoint, { amount_inr: parseInt(amount) });
      toast.success('Commitment updated successfully!');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update commitment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg font-bold text-[#0F172A] flex items-center gap-2">
            <Pencil className="h-4 w-4 text-brand" />
            Edit Commitment
          </DialogTitle>
          <DialogDescription className="text-[#64748B]">
            {commitment?.deal_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Commitment Amount (INR) *</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
              <Input
                type="text"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ''))}
                placeholder="Enter amount"
                className="pl-8 bg-white border-[#E2E8F0]"
              />
            </div>
            {amount && (
              <p className="text-xs text-gray-500">Amount: {formatINR(parseInt(amount))}</p>
            )}
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || !amount}
            className="!bg-[#10B981] hover:!bg-[#059669] !text-white"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Component to display commitments list (can be used in deal view or user view)
export function CommitmentsList({ 
  commitments = [], 
  type = 'deal', // 'deal' or 'user' - determines what info to show
  isAdmin = false,
  onEdit,
  onDelete,
  loading = false
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-brand" />
        <span className="ml-2 text-sm text-gray-500">Loading...</span>
      </div>
    );
  }

  if (commitments.length === 0) {
    return (
      <p className="text-sm text-[#64748B] italic py-2">No commitments yet.</p>
    );
  }

  return (
    <div className="space-y-2">
      {commitments.map((commitment, index) => (
        <div 
          key={commitment.id || index}
          className="flex items-center justify-between p-3 bg-[#F8F9FA] rounded-lg border border-[#E2E8F0]"
        >
          <div className="flex-1">
            {type === 'deal' ? (
              // Show investor info
              <div>
                <p className="font-medium text-[#0F172A] text-sm">{commitment.user_name || commitment.user_email}</p>
                <p className="text-xs text-[#64748B]">{commitment.user_email}</p>
              </div>
            ) : (
              // Show deal info
              <div>
                <p className="font-medium text-[#0F172A] text-sm">{commitment.deal_name}</p>
                <p className="text-xs text-[#64748B]">{commitment.deal_type}</p>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <Badge className="bg-green-100 text-green-700 border-green-300">
              {formatINR(commitment.amount_inr)}
            </Badge>
            
            {/* Edit button - always available */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit && onEdit(commitment)}
              className="h-7 w-7 p-0 text-brand hover:bg-brand/10"
            >
              <Pencil className="h-3 w-3" />
            </Button>
            
            {/* Delete button - only for admin */}
            {isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete && onDelete(commitment)}
                className="h-7 w-7 p-0 text-red-500 hover:bg-red-50"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
